<template>
  <div id="the-map-container">
      <div id="the-map-wrapper" :class="pipStateClass">
        <div v-if="pipActive" id="pip-controls" class="row no-margin-bottom">
          <div class="col s2">
            <a @click="togglePip">
              <i class="material-icons small">{{pipIcon}}</i>
            </a>
          </div>
          <div class="col s6">
            <small>{{formattedTime}} ({{timezoneOffset.timeZoneName}})</small>
          </div>
          <div class="col s4">
            <b class="right">Service Map</b>
          </div>
        </div>
        <div v-if="mapError" class="hero-map map-error">
          <p>{{ mapError }}</p>
        </div>
        <div v-else ref="searchMap" class="hero-map">
          <div v-if="drawingError" class="map-drawing-warning">
            <p>{{ drawingError }}</p>
          </div>
        </div>
        <slot name="controls"></slot>
      </div>
      <!-- default pip trigger -->
      <div id="map-bottom"></div>
  </div>
</template>

<script>
/* eslint no-undef: 0 */
import * as Sentry from '@sentry/browser'
import { mapGetters, mapActions, mapState } from 'vuex'
import { colors } from '../config/colors'
import { geocode } from '../services/googleMapsQuery'
import { createTerraDraw, destroyTerraDraw, getDrawingStyles } from '../utils/terraDraw'

export default {
  name: 'TheMap',
  props: {
    /**
     * The geometry of the general service area
     * @type {Object}
     */
    localeGeometry: {
      required: false,
      type: Object,
      default () { return {} }
    },
    msaGeometry: {
      required: false,
      type: Object,
      default () { return {} }
    },
    /**
     * The centroid point of the area
     * @type {Object}
     */
    locationGeometry: {
      required: false,
      type: Object
    },
    /**
     * A haulers service area geometry
     * @type {Array}
     */
    haulerGeometry: {
      required: true,
      type: Array,
      default () { return [] }
    },
    /**
     * The ids of the service areas that need to be hidden
     * @type {Array}
     */
    hiddenAreas: {
      required: false,
      type: Array,
      default: () => []
    },
    zoom: {
      required: false,
      type: Number,
      default: 10
    },
    /**
     * Whether polygon drawing tools should be active
     */
    drawing: {
      required: false,
      type: Boolean,
      default: false
    },
    /**
     * Drawing intent: merge additions or cut subtractions
     */
    drawingType: {
      required: false,
      type: String,
      default: ''
    },
    /**
     * The switch to turn on picture in picture
     *
     */
    pip: {
      required: false,
      type: Boolean,
      default: () => false
    },
    /**
     * The DOM element to trigger the picture in picture to show
     */
    pipTrigger: {
      required: false,
      type: String,
      default: () => '#map-bottom',
      validate (val) {
        // the value needs to be a valid DOM element
        return !!(document.getElementById(val))
      }
    }
  },
  data () {
    return {
      map: null,
      mapError: null,
      drawingError: null,
      terraDraw: null,
      marker: null,
      geojson: {},
      features: {},
      place_id: '',
      defaultStyle: () => ({
        fillColor: '#000000',
        fillOpacity: 0.50,
        strokeWeight: 1,
        visible: true,
        strokeOpacity: 1,
        zIndex: '-1'
      }),
      pipActive: false
    }
  },
  methods: {
    ...mapActions('session', ['togglePip']),
    getPlaceIdFromLatLng (prop, latLng) {
      const dragFilter = el => el.types.includes('locality') || el.types.includes('postal_code')
      const placeId = geocode('location', latLng)(dragFilter)
      return placeId
    },
    handlePlaceInfo (data) {
      this.resetMap()
      const payload = { place_id: data[0].place_id }
      this.$emit('locationChanged', payload)
      this.place_id = data[0].place_id
    },
    setPin () {
      if (!this.isMapReady()) return

      if (this.marker !== null) {
        this.marker.setMap(null)
      }
      if (this.locationGeometry !== null) {
        this.marker = new google.maps.Marker({
          map: this.map,
          title: 'Budget Dumpster Maps',
          position: this.locationGeometry,
          animation: google.maps.Animation.DROP,
          draggable: true
        })

        this.marker.addListener('click', this.enableStreetView)
        this.marker.addListener('dragend', this.markerDragListener)
        this.setZoomLevel(this.zoom)
      }
      return true
    },
    enableStreetView () {
      const streetView = this.map.getStreetView()

      streetView.setPosition(this.marker.getPosition())

      streetView.setVisible(true)

      streetView.setPov({
        heading: 265,
        pitch: 0,
        zoom: 8
      })
    },
    /**
     * Set up the inital map on the DOM element #search-map
     * @public
     * @returns {undefined} Operates on the reactive data property __map__
     */
    isMapReady () {
      return !this.mapError && this.map && this.map.data
    },
    setMap () {
      if (typeof google === 'undefined' || !google.maps) {
        throw new Error('Google Maps API is not available')
      }

      this.map = new google.maps.Map(this.$refs.searchMap, {
        center: new google.maps.LatLng(37.0902, -95.7129),
        zoom: 8,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        panControl: true,
        streetViewControl: true,
        zoomControl: true
      })

      this.map.data.setStyle({
        color: '#ffffff',
        fillOpacity: 0.6,
        strokeOpacity: 1.0,
        strokeWeight: 1
      })
    },
    /**
     * Clear the map and revert all styles
     * @public
     * @returns {undefined} Operates on the reactive data property __map__
     */
    clearMap () {
      if (!this.isMapReady()) return

      this.map.data.forEach(e => {
        this.map.data.remove(e)
      })
      if (this.marker !== null) {
        this.marker.setMap(null)
      }
      this.map.data.revertStyle()
    },
    /**
     * Add the locale geometry to the map instance
     * @public
     * @returns {undefined} Operates on the reactive data property __map__
     */
    setLocaleGeometry () {
      if (!this.isMapReady()) return

      if (Object.prototype.hasOwnProperty.call(this.localeGeometry, 'features') &&
            this.localeGeometry !== null) {
        this.localeGeometry.features[0].properties = { color: '#ffffff', className: 'locale-geometry' }
        this.map.data.addGeoJson(this.localeGeometry)
        let localeFeature
        this.map.data.forEach(e => {
          if (e.getProperty('className') === 'locale-geometry') {
            localeFeature = e
          }
        })

        this.map.data.overrideStyle(localeFeature, {
          fillOpacity: 0,
          strokeWeight: 3,
          strokeColor: '#ffffff',
          strokeOpacity: 1,
          visibile: true
        })
      }
    },
    setMsaGeometry () {
      if (!this.isMapReady()) return

      if (this.msaGeometry !== null && Object.prototype.hasOwnProperty.call(this.msaGeometry, 'features')) {
        this.map.data.addGeoJson(this.msaGeometry)

        let msaFeature

        this.map.data.forEach(e => {
          if (e.getProperty('className') === 'msa-geometry') {
            msaFeature = e
          }
        })

        this.map.data.overrideStyle(msaFeature, {
          fillOpacity: 0,
          strokeWeight: 3,
          strokeColor: '#8d8d8d',
          strokeOpacity: 1,
          visibile: true
        })
      }
    },
    /**
     * Method to set a feature for a specific hauler
     * @param {Object} feature
     */
    setHaulerStyle (feature, color) {
      this.map.data.overrideStyle(feature, {
        fillColor: color,
        fillOpacity: 0.3,
        strokeWeight: 1,
        visible: true,
        strokeOpacity: 1,
        zIndex: '-1'
      })
    },
    /**
     * Add the hauler geometry to the map instance as different colored layers
     * @public
     * @returns {undefined} Operates on the reactive data property __map__
     */
    setHaulerGeometry () {
      if (!this.isMapReady()) return

      const features = []

      this.haulerGeometry.forEach((area, key) => {
        if (!area || !area.features || !area.features[0]) return

        const color = area.features[0].properties.color || colors[key]
        area.features[0].properties = {
          color,
          className: 'service-area-overlay',
          id: key
        }
        this.map.data.addGeoJson(area)
      })

      this.map.data.forEach(feature => {
        if (feature.getProperty('className') === 'service-area-overlay') {
          features.push(feature)
          this.setHaulerStyle(feature, feature.getProperty('color'))
        }
      })

      this.features = features
      this.hideAreas(this.hiddenAreas)
    },
    /**
     * Full reset of the map instance
     * @public
     * @returns {undefined} Operates on the reactive data property __map__
     */
    resetMap () {
      if (!this.isMapReady()) return

      this.clearMap()
      if (this.marker) {
        this.marker.setMap(null)
      }
      this.setPin()
      this.setHaulerGeometry()
      this.setLocaleGeometry()
      this.setMsaGeometry()
    },
    /**
     * Event handler for a pin reposition on the map instance
     * @public
     * @param  {object}
     * @return {undefined} Queries gmaps places api and calls __handlePlaceInfo__
     */
    markerDragListener (ev) {
      this.clearMap()
      this.getPlaceIdFromLatLng('location', ev.latLng)
        .then(this.handlePlaceInfo)
        .catch(e => Sentry.captureException(e))
    },
    /**
     * Sets the zoom level for the map
     * @param {Integer} zoom a number from 1 to 10 to set the zoom level
     * @returns null
     */
    setZoomLevel (zoom) {
      if (this.marker.position !== null) {
        this.map.setCenter(this.marker.position)
      }
      this.map.setZoom(zoom)
    },
    hideAreas (val) {
      if (!this.isMapReady()) return

      this.map.data.forEach(e => {
        if (val.includes(e.getId())) {
          this.map.data.overrideStyle(e, {
            visible: false,
            strokeOpacity: 0,
            fillOpacity: 0
          })
        }

        if (e.getProperty('className') !== 'msa-geometry' &&
        e.getProperty('className') !== 'locale-geometry' &&
        !val.includes(e.getId())) {
          this.setHaulerStyle(e, e.getProperty('color'))
        }
      })
    },
    setupPictureInPicture () {
      const trigger = $(`${this.pipTrigger}`).offset().top
      $(window).scroll(() => {
        if ($(window).scrollTop() > trigger && !this.pipActive) {
          // $('#the-map-wrapper').addClass('pip');
          this.pipActive = true
        } else if ($(window).scrollTop() < trigger && this.pipActive) {
          // $('#the-map-wrapper').removeClass('pip');
          this.pipActive = false
        }
      })
    },
    disablePictureInPicture () {
      $(window).off()
      this.pipActive = false
    },
    handlePolygonFinish (id) {
      if (!this.terraDraw) return

      const feature = this.terraDraw.getSnapshotFeature(id)
      if (!feature || feature.geometry.type !== 'Polygon') return

      const payload = {
        label: this.drawingType === 'cut' ? 'Custom Cut Drawing' : 'Custom Merge Drawing',
        category: 'Custom Drawing',
        place_id: null,
        search_term: this.drawingType === 'cut' ? 'Custom Cut' : 'Custom Merge',
        the_geom: feature.geometry
      }

      if (this.drawingType === 'merge') {
        this.$emit('custom_area_merged', payload)
      } else if (this.drawingType === 'cut') {
        this.$emit('custom_area_cut', payload)
      }

      this.terraDraw.removeFeatures([id])
    },
    initTerraDraw () {
      if (!this.isMapReady() || this.terraDraw) return

      try {
        this.terraDraw = createTerraDraw(this.map, {
          drawingType: this.drawingType,
          onFinish: this.handlePolygonFinish
        })
        this.drawingError = null
      } catch (e) {
        this.drawingError = 'Boundary drawing tools are unavailable. Zip-code service region management is still available below.'
        this.$emit('drawing-error', e)
        Sentry.captureException(e)
      }
    },
    enableDrawing () {
      this.initTerraDraw()
      if (!this.terraDraw) return

      this.terraDraw.updateModeOptions('polygon', {
        styles: getDrawingStyles(this.drawingType)
      })
      this.terraDraw.setMode('polygon')
    },
    disableDrawing () {
      if (!this.terraDraw) return

      destroyTerraDraw(this.terraDraw)
      this.terraDraw = null
    },
    setDrawingMode (mode) {
      this.initTerraDraw()
      if (!this.terraDraw) return

      this.terraDraw.setMode(mode)
    }
  },
  computed: {
    ...mapState('session', ['preferences']),
    ...mapGetters(['hoverState', 'getHoverId', 'getPrevId']),
    ...mapGetters('time', ['formattedTime']),
    ...mapState('time', ['timezoneOffset']),
    pipStateClass () {
      return {
        pip: this.pipActive,
        minimize: this.preferences.showPip === true,
        maximize: this.preferences.showPip === false
      }
    },
    pipIcon () {
      return this.preferences.showPip ? 'add' : 'minimize'
    },
    readyToRender () {
      return !!(this.localeGeometry) &&
      !!(this.msaGeometry) &&
      !!(this.haulerGeometry) &&
      !!(this.localeLongitude) &&
      !!(this.localeLatitude)
    }
  },
  watch: {
    drawing (val) {
      if (!this.isMapReady()) return

      if (val) {
        this.enableDrawing()
      } else {
        this.disableDrawing()
      }
    },
    drawingType () {
      if (!this.terraDraw || !this.drawing) return

      this.terraDraw.updateModeOptions('polygon', {
        styles: getDrawingStyles(this.drawingType)
      })
    },
    getHoverId (val) {
      if (!this.isMapReady()) return

      const feature = this.features
        .filter(f => f.getId() === val)
        .pop()

      if (!feature || this.hiddenAreas.includes(val)) return

      this.map.data.overrideStyle(feature, {
        strokeWeight: 3
      })
    },
    hoverState (val) {
      if (!this.isMapReady()) return

      const feature = this.features
        .find(f => f.getId() === this.getPrevId)

      if (!feature) return

      if (val === 'off') {
        this.setHaulerStyle(feature, feature.getProperty('color'))
      } else {
        this.map.data.overrideStyle(feature, {
          fillColor: feature.getProperty('color'),
          fillOpacity: 0.3,
          strokeWeight: 3,
          visible: true,
          strokeOpacity: 1,
          zIndex: '-1'
        })
      }
    },
    locationGeometry () {
      this.setPin()
    },
    localeGeometry () {
      this.setLocaleGeometry()
    },
    msaGeometry () {
      this.setMsaGeometry()
    },
    haulerGeometry () {
      this.clearMap()
      this.setHaulerGeometry()
      this.setPin()
    },
    hiddenAreas (val) {
      this.hideAreas(val)
    },
    pip (applied) {
      if (applied) {
        return this.setupPictureInPicture()
      }

      return this.disablePictureInPicture()
    }
  },
  mounted () {
    try {
      this.setMap()
      this.setHaulerGeometry()
      this.map.data.addListener('mouseover', e => {
        if (e.feature.getProperty('className') !== 'locale-geometry' &&
        e.feature.getProperty('className') !== 'msa-geometry') {
          this.map.data.overrideStyle(e.feature, { strokeWeight: 3 })
        }
      })

      this.map.data.addListener('mouseout', e => {
        if (e.feature.getProperty('className') !== 'locale-geometry' &&
        e.feature.getProperty('className') !== 'msa-geometry') {
          this.map.data.overrideStyle(e.feature, { strokeWeight: 1 })
        }
      })

      if (this.pip) this.setupPictureInPicture()
    } catch (e) {
      this.mapError = 'Unable to load the map. Zip-code service region management is still available below.'
      Sentry.captureException(e)
    }
  },
  beforeDestroy () {
    this.disableDrawing()
  }
}
</script>

<style lang="scss">
@import 'src/styles/base/_variables.scss';
$map-height: 500px;
$controls-height: 35px;
#the-map-container {
  height: $map-height;
}
    .hero-map {
        position: relative;
        width: 100%;
        height: $map-height;
        margin-bottom: 10px;
    }
    .map-error {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f5f5;
        color: #616161;
        text-align: center;
        padding: 1rem;
    }
    .map-drawing-warning {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1;
        background-color: rgba(250, 141, 41, 0.95);
        color: #fff;
        text-align: center;
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
    }
    #the-map-wrapper.pip{
      position: fixed;
      bottom: 0;
      right: 0;
      width:30%;
      z-index:999;
      box-shadow:5px 5px 8px grey;

      &.minimize{
        height:$controls-height;
      }

      #pip-controls{
        width:100%;
        color: white;
        background-color: $secondary;
        padding: 0 15px;
        height:$controls-height;
      }
    }

</style>
